import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { spErrorTags } from "@eventr/common";

/** Where chat pictures are kept on the device, under the app's cache directory. */
const CHAT_MEDIA_DIR = "chat-images";

/** One flat, filesystem-safe name per file's server path, which names a file uniquely. */
const pathFor = (key: string) => `${CHAT_MEDIA_DIR}/${key.replace(/^\/+/, "").replace(/[^A-Za-z0-9._-]/g, "_")}`;

/**
 * The chat library's `mediaCache`: a picture's device copy, downloaded the first time this
 * device shows it, so a thread's photos show offline. A file's path never changes what it holds,
 * so a copy is kept until logout. Web leaves pictures to the browser's cache.
 */
export async function cacheChatMedia(src: string, key: string): Promise<string | undefined> {
  if (!Capacitor.isNativePlatform()) return undefined;
  const path = pathFor(key);
  try {
    const { uri } = await Filesystem.stat({ path, directory: Directory.Cache });
    return Capacitor.convertFileSrc(uri);
  } catch {
    // Not on the device yet.
  }
  try {
    // Native, so the proxy's response needs no CORS headers. The URL carries its own token.
    const { path: written } = await Filesystem.downloadFile({
      url: src,
      path,
      directory: Directory.Cache,
      recursive: true,
    });
    const uri = written ?? (await Filesystem.getUri({ path, directory: Directory.Cache })).uri;
    return Capacitor.convertFileSrc(uri);
  } catch (error) {
    spErrorTags("Failed to keep a chat picture on the device", ["rocketchat", "FILES"], error);
    return undefined;
  }
}

/** Deletes every chat picture kept on the device. Called on logout, with the chat database. */
export async function clearChatMediaCache(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  await Filesystem.rmdir({ path: CHAT_MEDIA_DIR, directory: Directory.Cache, recursive: true }).catch(() => {
    // Nothing kept yet.
  });
}
